import { z } from 'zod';
import { currencyStepCents } from './currency.ts';
import { firstCleanPriceSchema } from './first-clean-price.ts';

const squareFeet = z.number().int().min(1).max(1_000_000);
const rate = z.number().int().min(1).max(100_000_000);

/** Deliberately unset until the company supplies all four commercial inputs. */
export const areaPricingRulesSchema = z
  .object({
    thresholdSquareFeet: squareFeet,
    defaultRateCents: rate,
    minRateCents: rate,
    maxRateCents: rate,
  })
  .strict()
  .refine(
    (value) => value.minRateCents <= value.defaultRateCents && value.defaultRateCents <= value.maxRateCents,
    'The default area rate must be inside the minimum and maximum rates',
  );

export type AreaPricingRules = z.infer<typeof areaPricingRulesSchema>;

export function areaRatePrecisionProblem(rules: AreaPricingRules, currencyCode: string): string | null {
  const step = currencyStepCents(currencyCode);
  return [rules.minRateCents, rules.defaultRateCents, rules.maxRateCents].some((rate) => rate % step !== 0)
    ? 'Area rates must use the supported precision of the quote currency.'
    : null;
}

/** Settings cards omit unrelated fields; a currency change clears stale rates. */
export function areaPricingRulesChanges(
  input: { areaPricingRules?: AreaPricingRules | null | undefined; currencyCode?: string | undefined },
  currentCurrency: string,
): Record<string, string | null> {
  if (input.areaPricingRules !== undefined)
    return {
      areaPricingRulesStored:
        input.areaPricingRules === null
          ? null
          : JSON.stringify({ currencyCode: input.currencyCode ?? currentCurrency, rules: input.areaPricingRules }),
    };
  return input.currencyCode !== undefined && input.currencyCode !== currentCurrency
    ? { areaPricingRulesStored: null }
    : {};
}

export const quoteAreaPricingInputSchema = z
  .object({
    mode: z.enum(['hourly', 'area']),
    squareFeet: squareFeet.nullable(),
    rateCents: rate.nullable(),
    confirmed: z.boolean(),
    /**
     * The first clean price, from before it moved to the quote's own
     * `firstCleanPrice` (0105). Still accepted from an older client, which
     * the quote save turns into the quote's first clean price; never stored.
     */
    firstClean: firstCleanPriceSchema.nullable().optional(),
  })
  .strict();

const snapshotSchema = quoteAreaPricingInputSchema.extend({
  minRateCents: rate.nullable(),
  maxRateCents: rate.nullable(),
});
export type QuoteAreaPricing = z.infer<typeof snapshotSchema>;
export type QuoteAreaPricingInput = z.infer<typeof quoteAreaPricingInputSchema>;

export const HOURLY_AREA_PRICING: QuoteAreaPricing = {
  mode: 'hourly',
  squareFeet: null,
  rateCents: null,
  confirmed: true,
  minRateCents: null,
  maxRateCents: null,
};

/** Reads only company-owned configuration in the currency it was configured for. */
export function readAreaPricingRules(json: string | null | undefined, currencyCode: string): AreaPricingRules | null {
  if (!json) return null;
  const parsed: unknown = JSON.parse(json);
  const stored = z.object({ currencyCode: z.string(), rules: areaPricingRulesSchema }).strict().parse(parsed);
  return stored.currencyCode === currencyCode ? stored.rules : null;
}

export function readQuoteAreaPricing(json: string | null | undefined): QuoteAreaPricing {
  if (!json) return { ...HOURLY_AREA_PRICING };
  // A first clean price stored here before 0105 moved it onto the quote is
  // not this snapshot's any more; the next save drops it from the column.
  const { firstClean: _moved, ...area } = snapshotSchema.parse(JSON.parse(json));
  return area;
}

/** A property's own rate, kept inside the company range; blank uses the default. */
export function propertyAreaRateCents(
  rules: AreaPricingRules,
  propertyRateCents: number | null | undefined,
  currencyCode?: string,
): number {
  if (
    propertyRateCents == null ||
    !Number.isSafeInteger(propertyRateCents) ||
    propertyRateCents < 1 ||
    // A rate saved under another currency's precision is not this one's rate.
    (currencyCode !== undefined && propertyRateCents % currencyStepCents(currencyCode) !== 0)
  )
    return rules.defaultRateCents;
  return Math.min(rules.maxRateCents, Math.max(rules.minRateCents, propertyRateCents));
}

/**
 * Area pricing works for per-visit and monthly quotes alike: a monthly price
 * is the per-visit area price times the visits the month holds. The property's
 * own rate, when it has one, replaces the company default.
 */
export function seedQuoteAreaPricing(
  rules: AreaPricingRules | null,
  measuredSquareFeet: number | null,
  _perVisit: boolean,
  propertyRateCents?: number | null,
  currencyCode?: string,
): QuoteAreaPricing {
  if (!rules) return { ...HOURLY_AREA_PRICING };
  const size = squareFeet.safeParse(measuredSquareFeet);
  return {
    mode: size.success && size.data >= rules.thresholdSquareFeet ? 'area' : 'hourly',
    squareFeet: size.success ? size.data : null,
    rateCents: propertyAreaRateCents(rules, propertyRateCents, currencyCode),
    minRateCents: rules.minRateCents,
    maxRateCents: rules.maxRateCents,
    // A missing measurement never silently chooses the threshold's lower side.
    confirmed: size.success,
  };
}

/**
 * Square-foot pricing chosen but not measured yet.
 *
 * Choosing "By square foot" saves straight away, before anybody has had a
 * chance to type the area, and a property of unknown size has none to fill in.
 * That is an unfinished draft, not a bad request: the save keeps the choice and
 * the quote stays blocked ("Pricing decision required") until the area is
 * entered. Sharing and accepting still refuse it (assertShareable and the
 * public accept both check areaPricingProblem).
 */
export function areaAwaitingMeasurement(value: QuoteAreaPricing): boolean {
  return value.confirmed && value.mode === 'area' && value.squareFeet === null;
}

/**
 * What a save must refuse: every area pricing problem except the missing area
 * itself. The rest of a square-foot choice (rate inside the saved range,
 * company bounds, currency precision) is checked as if an
 * area were already there, so a draft waiting for its measurement can never
 * hold a value that would be refused once the area is entered.
 */
export function areaPricingSaveProblem(value: QuoteAreaPricing, monthly = false, currencyCode = 'USD'): string | null {
  return areaAwaitingMeasurement(value)
    ? areaPricingProblem({ ...value, squareFeet: 1 }, monthly, currencyCode)
    : areaPricingProblem(value, monthly, currencyCode);
}

export function areaPricingProblem(value: QuoteAreaPricing, _monthly = false, currencyCode = 'USD'): string | null {
  if (value.mode !== 'area') return null;
  if (value.squareFeet === null) return 'Enter the measured area before using area pricing.';
  if (value.rateCents === null || value.minRateCents === null || value.maxRateCents === null)
    return 'Configure the company area rate and range before using area pricing.';
  if (value.rateCents < value.minRateCents || value.rateCents > value.maxRateCents)
    return "The area rate must be inside this quote's saved rate range.";
  if (value.rateCents % currencyStepCents(currencyCode) !== 0)
    return 'Area rates must use the supported precision of the quote currency.';
  if (BigInt(value.squareFeet) * BigInt(value.rateCents) > 100_000_000n)
    return 'The area subtotal exceeds the supported quote amount.';
  return null;
}

/** Bounds are server-owned snapshots and cannot be overwritten by the editor. */
export function changeQuoteAreaPricing(current: QuoteAreaPricing, input: QuoteAreaPricingInput): QuoteAreaPricing {
  return { ...current, ...quoteAreaPricingInputSchema.parse(input) };
}

export function areaPricingCalculation(value: QuoteAreaPricing, monthly: boolean, currencyCode = 'USD') {
  const pricingBlocked = !value.confirmed || areaPricingProblem(value, monthly, currencyCode) !== null;
  return {
    pricingBlocked,
    areaPricing:
      !pricingBlocked && value.mode === 'area' && value.squareFeet !== null && value.rateCents !== null
        ? { squareFeet: value.squareFeet, rateCents: value.rateCents }
        : null,
  };
}

/**
 * A square-foot price replaces every room's own price, so a quote priced by
 * area carries no flat room prices. Residential quotes are usually built from
 * room types that come with one, which is why the editor clears them when the
 * estimator picks square-foot pricing (and on any room added afterwards)
 * instead of hiding the choice. Returns the same object when nothing changes.
 */
export function withoutAreaRoomPrices<
  T extends {
    readonly areaPricing?: QuoteAreaPricing | null | undefined;
    readonly rooms: readonly { readonly flatPriceCents: number | null; readonly chargeCents?: number | null }[];
  },
>(quote: T): T {
  const area = quote.areaPricing;
  if (!area || area.mode !== 'area' || !area.confirmed) return quote;
  if (!quote.rooms.some((room) => room.flatPriceCents !== null)) return quote;
  return {
    ...quote,
    rooms: quote.rooms.map((room) =>
      room.flatPriceCents === null ? room : { ...room, flatPriceCents: null, chargeCents: null },
    ),
  } as T;
}
